import { useParams } from 'react-router-dom';

import { REMOTE_PERMISSIONS } from '@backrs_ui/common-ui';

import { useHasPermission } from '@backrs_ui/grow-ui/src/hooks/useHasPermission';
import { useOperatorInstitutionSwitch } from '@backrs_ui/grow-ui/src/hooks/useOperatorInstitutionSwitch';
import {
  useGetAssigneeQuery,
  useGetInstitutionMemberQuery,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import {
  type Assignee,
  type InstitutionMember,
} from '@backrs_ui/grow-ui/src/redux/coachingDashboard/coachingDashboard.types';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  decodeOperatorContextRefusal,
  type HeldRoot,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { selectIsPlatformContext } from '@backrs_ui/grow-ui/src/redux/operatorContext';
import { isRefusalOrMissing, statusOf } from '@backrs_ui/grow-ui/src/utils/fetch/refusal';

import { skipToken } from '@reduxjs/toolkit/query';

/**
 * The member read is refused against the organization in use; the assignee
 * read against the caller's own assignments.
 */
export type MemberRefusal = 'inOrganization' | 'access';

export type CurrentMember = Readonly<{
  member: Assignee | InstitutionMember | undefined;
  isCoach: boolean;
  canViewCoaches: boolean;
  canManageCoaches: boolean;
  isLoading: boolean;
  isError: boolean;
  refusal: MemberRefusal | null;
  /** Roots offered when the read refused under the context the tab holds. */
  operatorContextRoots: HeldRoot[] | null;
  onSelectOperatorInstitution: (institutionId: number) => void;
}>;

const isForbidden = (error: unknown): boolean => statusOf(error) === 403;

export const useCurrentMember = (): CurrentMember => {
  const { memberId } = useParams<{ memberId: string }>();

  const isInstitutionAdmin = useHasPermission(REMOTE_PERMISSIONS.MANAGE_INSTITUTION_MEMBERS);
  const canAccessCoachAssignees = useHasPermission(REMOTE_PERMISSIONS.ACCESS_COACH_ASSIGNEES);

  const isPlatformMode = useAppSelector(selectIsPlatformContext);

  const parsedId = memberId && !isNaN(Number(memberId)) ? Number(memberId) : null;

  const {
    data: institutionMember,
    isLoading: isLoadingInstitutionMember,
    isError: isErrorInstitutionMember,
    error: institutionMemberError,
  } = useGetInstitutionMemberQuery(
    parsedId !== null && isInstitutionAdmin ? { userId: parsedId } : skipToken,
  );

  const isRefusedInOrganization = isForbidden(institutionMemberError);
  /**
   * An assignment is a direct relationship between two people, so no operator context
   * reaches it. A caller holding both grants who is refused the member under their
   * context may still be this member's assigned coach.
   */
  const fallsBackToAssignment =
    isInstitutionAdmin && canAccessCoachAssignees && isRefusedInOrganization;

  const {
    data: assignee,
    isLoading: isLoadingAssignee,
    isError: isErrorAssignee,
    error: assigneeError,
  } = useGetAssigneeQuery(
    parsedId !== null && canAccessCoachAssignees && (!isInstitutionAdmin || isRefusedInOrganization)
      ? parsedId
      : skipToken,
  );

  const onSelectOperatorInstitution = useOperatorInstitutionSwitch();

  // Only a refusal to a request that already carried a context reaches here:
  // a bare one is defaulted and retried before it ever surfaces as an error.
  const operatorContextRoots = decodeOperatorContextRefusal(institutionMemberError);

  const isRefusedAssignee = isForbidden(assigneeError);
  const assigneeAnsweredRefusal = isRefusalOrMissing(assigneeError);

  const member = assignee ?? institutionMember;
  const isLoading = isLoadingAssignee || isLoadingInstitutionMember;
  const isError = fallsBackToAssignment
    ? isErrorAssignee
    : isErrorAssignee || isErrorInstitutionMember;
  const refusal: MemberRefusal | null = isRefusedInOrganization
    ? fallsBackToAssignment && !assigneeAnsweredRefusal
      ? null
      : 'inOrganization'
    : isRefusedAssignee
      ? 'access'
      : null;

  const isCoach = canAccessCoachAssignees || isInstitutionAdmin;

  return {
    member,
    isCoach,
    canViewCoaches: isInstitutionAdmin,
    // Platform mode holds no reach to write into, and an assignment carries none either.
    canManageCoaches: isInstitutionAdmin && !isPlatformMode && !fallsBackToAssignment,
    isLoading,
    isError,
    refusal,
    operatorContextRoots,
    onSelectOperatorInstitution,
  };
};
